import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/Button";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import InputAdornment from "@mui/material/InputAdornment";
import KeyIcon from "@mui/icons-material/Key";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import ForgotPasswordModal from "../ForgotPasswordModal/ForgotPasswordModal";
import {
  clearLocalStorage,
  enforceMaxLength,
  getMaxLengthAttemptHandlers,
  getMaxLengthExceededMessage,
  setAccessToken,
  setIsAdmin,
  useMaxLengthExceeded,
} from "../../../../utills";
import { PASSWORD_MAX_LENGTH } from "../../../../constants";
import useAdminLogin from "../../../../apis/auth/useAdminLogin";

interface LoginFormProps {
  onModalClose: () => void;
}

const AdminLoginForm: FC<LoginFormProps> = ({ onModalClose }) => {
  const login = useAdminLogin();
  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement | null>(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [passwordLimitExceeded, setPasswordLimitExceeded] =
    useMaxLengthExceeded();
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { emailAddress: "", password: "" },
  });

  const onSubmit = useCallback(
    (data: { emailAddress: string; password: string }) => {
      login.mutate({
        emailId: data.emailAddress.trim(),
        password: data.password.trim(),
      });
    },
    [login]
  );

  const openForgotPasswordModal = useCallback(() => {
    setIsForgotPasswordModalOpen(true);
    onModalClose();
  }, [onModalClose]);

  const closeForgotPasswordModal = useCallback(() => {
    setIsForgotPasswordModalOpen(false);
  }, []);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage("");
  }, []);

  useEffect(() => {
    if (login.isSuccess) {
      const user = login.data;

      clearLocalStorage();
      setAccessToken(user.accessToken);
      if (user.userrole === "admin") {
        setIsAdmin("admin");
      }

      // setIndividualUser(user.individual.toString());
      //Add admin var to local Storage
      reset();
      onModalClose();
      navigate("/admin/pendingrequests");
    }
    // eslint-disable-next-line
  }, [login.isSuccess]);

  useEffect(() => {
    if (login.isError && login.error?.response?.data) {
      setErrorMessage(login.error?.response?.data as string);
    }
    // eslint-disable-next-line
  }, [login.isError]);

  useEffect(() => {
  if (errorMessage) {
    errorRef.current?.focus();
  }
}, [errorMessage]);

  return (
    <>
      <h2 className="text-center">Admin Area</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
        <div className="filed-slide mb-4">
          <Controller
            name="emailAddress"
            control={control}
            rules={{
              required: "Email is required.",
              pattern: {
                //value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, Commented for PR 5641742
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address.",
              },
            }}
            render={({ field, fieldState }) => (
              <>
              <label className="sr-only" htmlFor="admin-emailAddress">
      eFileIL admin email address
          </label>
              <TextField
                className="w-100 m-0"
                id="admin-emailAddress"
                inputProps={{ id: "admin-emailAddress" }}
                {...field}
                onBlur={() => {
                  field.onChange(field.value?.trim());
                  field.onBlur();
                }}
                placeholder="Your eFileIL admin email address"
                label="eFileIL admin email address"
                variant="standard"
                error={!!fieldState.error}
                helperText={fieldState.error ? fieldState.error.message : null}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <MarkunreadIcon />
                    </InputAdornment>
                  ),
                }}
              />
              </>
            )}
          />
        </div>
        <div className="filed-slide">
          <Controller
            name="password"
            control={control}
            rules={{
              required: "Password is required.",
            }}
            render={({ field, fieldState }) => (
              <>
              <label className="sr-only" htmlFor="admin-password">
      Your eFileIL admin Password
    </label>
              <TextField
                id="admin-password"
                // PR 12234 Start
                inputProps={{
                  id: "admin-password",
                  maxLength: PASSWORD_MAX_LENGTH,
                  ...getMaxLengthAttemptHandlers(
                    field.value ?? "",
                    PASSWORD_MAX_LENGTH,
                    setPasswordLimitExceeded
                  ),
                }}
                // PR 12234 End
                {...field}
                // PR 12234 Start
                onChange={(e) =>
                  enforceMaxLength(
                    e.target.value,
                    PASSWORD_MAX_LENGTH,
                    field.onChange,
                    setPasswordLimitExceeded
                  )
                }
                // PR 12234 End
                onBlur={() => {
                  field.onChange(field.value?.trim());
                  field.onBlur();
                }}
                placeholder="Your ODYSSEY efileIL admin Password"
                className="w-100 m-0"
                label="Your eFileIL admin Password"
                variant="standard"
                error={!!fieldState.error}
                // PR 12234 Start
                helperText={
                  fieldState.error ? (
                    fieldState.error.message
                  ) : passwordLimitExceeded ? (
                    <Box component="span" sx={{ color: "warning.main" }}>
                      {getMaxLengthExceededMessage("Password")}
                    </Box>
                  ) : null
                }
                // PR 12234 End
                type="password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon />
                    </InputAdornment>
                  ),
                }}
              />
              </>
            )}
          />
        </div>
        <div className="mt-4 mb-4 ">
          <div className="forgot-link justify-content-between d-flex">
            {/* <span
              role="button"
              className="theme-grey cursor-pointer"
              onClick={openForgotPasswordModal}
            >
              Forgot Password?
            </span> */ /* Commented for PR_12032 */}

            {/* PR_12032 Start */}
            <button
                type="button"
                className="theme-grey cursor-pointer btn-forgot-pass"
                onClick={openForgotPasswordModal}>
                Forgot Password?
              </button>
            {/* PR_12032 End */}
          </div>
        </div>
        <div className="filed-slide text-center">
          <IconButton
            className="btn primary-button"
            type="submit"
            disabled={login.isLoading}
          >
            {login.isLoading ? (
              <CircularProgress
                sx={{
                  height: "25px !important",
                  width: "25px !important",
                  "--CircularProgress-size": "8px",
                  "--CircularProgress-trackThickness": "1px",
                  "--CircularProgress-progressThickness": "1px",
                }}
                color="inherit"
              />
            ) : (
              "Login"
            )}
          </IconButton>
        </div>
      </form>

      <ForgotPasswordModal
        isModalOpen={isForgotPasswordModalOpen}
        closeModal={closeForgotPasswordModal}
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={!!errorMessage}
        autoHideDuration={10000}
        onClose={clearErrorMessage}
      >
        <Alert ref={errorRef} tabIndex={-1} onClose={clearErrorMessage} severity="error" variant="filled" aria-live="assertive" role="alert">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminLoginForm;
